import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { catchError, switchMap, tap, takeUntil } from 'rxjs/operators';
import { ToastrService } from './toastr.service';
import { WalletApiIntegrteService } from './wallet-apiintegrte-service';
import { LocalStorageService } from './local-storage.service';
import { WalletSessionSecurityService } from './wallet-session-security.service';

function readCookie(name: string): string | null {
  const source = typeof document !== 'undefined' ? document.cookie : '';
  if (!source) return null;
  const parts = source.split(';');
  for (const part of parts) {
    const [rawKey, ...rest] = part.trim().split('=');
    if (rawKey === name) {
      const value = rest.join('=');
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }
  return null;
}

export const httpLoggingInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const toastr = inject(ToastrService);
  const walletAuthService = inject(WalletApiIntegrteService);
  const localStorageService = inject(LocalStorageService);
  const walletSessionSecurity = inject(WalletSessionSecurityService);
  let headers = req.headers;
  const method = req.method.toUpperCase();
  const isMutating =
    method === 'GET' ||
    method === 'POST' ||
    method === 'PUT' ||
    method === 'PATCH' ||
    method === 'DELETE';

  const xsrfCookie = readCookie('csrf_token');

  if (xsrfCookie) {
    const token = xsrfCookie.includes('|')
      ? xsrfCookie.split('|')[0]
      : xsrfCookie;

    headers = headers.set('x-xsrf-token', token);
  } else if (isMutating) {
    console.warn('CSRF-Token not found!');
  }

  req = req.clone({
    headers,
    withCredentials: true,
  });

  const isLogoutRequest = req.url.endsWith('/auth/logout');
  const hasAuthSession = !!localStorageService.getToken() && !isLogoutRequest;

  return from(
    hasAuthSession ? walletAuthService.requireValidAuthenticatedSession() : Promise.resolve(true)
  ).pipe(
    switchMap((isValid) => {
      if (!isValid) {
        localStorageService.removeToken();
        localStorageService.removeAuthenticatedWallet();
        
        if (!walletSessionSecurity.isInvalidationInProgress()) {
          void walletSessionSecurity.forceSessionInvalidation('Session expired. Please reconnect.');
        }

        return throwError(
          () =>
            new HttpErrorResponse({
              status: 401,
              statusText: 'Unauthorized',
              url: req.url,
            })
        );
      }

      return next(req).pipe(
        takeUntil(walletSessionSecurity.sessionInvalidation$),
        tap((event: HttpEvent<unknown>) => {
          if (event instanceof HttpResponse) {
            console.log(`[HTTP] STATUS ${event.status} ${event.statusText} | ${req.url}`);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(`[HTTP] ERROR ${error.status} | ${req.url}`);
          console.error('[HTTP] Error body:', error.error);

          let message = 'An unexpected error occurred';

          switch (error.status) {
            case 400:
              message = error.error?.message || 'Invalid Request';
              toastr.showToast(message, 'error');
              break;

            case 401:
              localStorageService.removeToken();
              localStorageService.removeAuthenticatedWallet();
              message = 'You are not authorized. Please log in again.';
              toastr.showToast(message, 'error');
              break;

            case 403:
              message = 'Access forbidden. You do not have permission.';
              toastr.showToast(message, 'error');
              break;

            case 404:
              if (req.method.toUpperCase() !== 'GET') {
                message = error.error?.message || 'Resource Not Found';
                toastr.showToast(message, 'error');
              }
              break;

            case 500:
              message = 'Internal Server Error. Please try again later.';
              toastr.showToast(message, 'error');
              break;

            case 0:
              message = 'Cannot reach the server. Check your network or backend.';
              toastr.showToast(message, 'error');
              break;

            default:
              toastr.showToast(error.error?.message || message, 'error');
          }
          return throwError(() => error);
        })
      );
    })
  );
};